import React, { useEffect, useState } from "react";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

/**
 * Universal "request access" modal. Use whenever a feature isn't shipped yet
 * (Enable / Upgrade / Contact us / Beta access). Submits to
 * POST /api/admin/beta/request and shows a "You're on the list" success state.
 */
export default function BetaRequestModal({
    open,
    onClose,
    feature,
    title,
    description,
    submitLabel = "Request access",
    testid = "beta-modal",
}) {
    const [form, setForm] = useState({ name: "", email: "", notes: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({ name: "", email: "", notes: "" });
            setDone(false);
        }
    }, [open]);

    if (!open) return null;

    const submit = async (e) => {
        e?.preventDefault();
        setSubmitting(true);
        try {
            await adminApi.post("/api/admin/beta/request", {
                feature,
                name: form.name,
                email: form.email,
                notes: form.notes,
            });
            setDone(true);
            toast.success("Request received — our team will be in touch within 1 business day.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to submit");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid={testid}
        >
            <div
                className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between mb-2">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-stone-500">
                            Request access
                        </div>
                        <h3 className="font-heading text-2xl text-orchid-700">{title}</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-stone-400 hover:text-stone-700"
                        data-testid={`${testid}-close`}
                    >
                        <X size={18} />
                    </button>
                </div>

                {done ? (
                    <div className="py-6 text-center" data-testid={`${testid}-done`}>
                        <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/10 text-emerald-600 flex items-center justify-center mb-3">
                            <CheckCircle2 size={26} />
                        </div>
                        <div className="font-medium text-stone-900">You&apos;re on the list</div>
                        <p className="text-xs text-stone-500 mt-2 max-w-xs mx-auto">
                            Our team will reach out within 1 business day to set up{" "}
                            <span className="font-medium">{title}</span>.
                        </p>
                        <button
                            onClick={onClose}
                            className="mt-5 text-sm font-medium px-4 py-2 rounded-md border border-stone-200 hover:bg-stone-50"
                        >
                            Close
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit}>
                        <p className="text-xs text-stone-500 mb-5">
                            {description ||
                                "Tell us a bit about your use case and our team will reach out within 1 business day."}
                        </p>
                        <div className="space-y-3">
                            <input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Your name"
                                data-testid={`${testid}-name`}
                                className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                            />
                            <input
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder="Email"
                                data-testid={`${testid}-email`}
                                className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                            />
                            <textarea
                                rows={3}
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                placeholder="Anything we should know?"
                                data-testid={`${testid}-notes`}
                                className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                            />
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button
                                type="button"
                                onClick={onClose}
                                className="text-sm px-4 py-2 rounded-md border border-stone-200 text-stone-700 hover:bg-stone-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid={`${testid}-submit`}
                                className="text-sm font-medium px-4 py-2 rounded-md bg-orchid-700 hover:bg-orchid-600 text-white disabled:opacity-50 inline-flex items-center gap-2"
                            >
                                {submitting ? <Loader2 size={13} className="animate-spin" /> : null}
                                {submitLabel}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
